import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ron, STATUS_LABELS, DOCUMENTS_STATUS_MAP } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Car as CarIcon } from "lucide-react";
import { CAR_GROUPS, carGroup } from "@/lib/carStatus";

export default function Cars() {
  const [cars, setCars] = useState([]);
  const [filter, setFilter] = useState({ status: "", make: "", q: "" });
  const [group, setGroup] = useState("available");

  async function load() {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.make) params.make = filter.make;
    if (filter.q) params.q = filter.q;
    const { data } = await api.get("/cars", { params });
    setCars(data);
  }

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
  }, [filter]);

  const counts = CAR_GROUPS.reduce((acc, g) => ({ ...acc, [g.key]: g.key === "all" ? cars.length : cars.filter((c) => carGroup(c.status) === g.key).length }), {});
  const visible = group === "all" ? cars : cars.filter((c) => carGroup(c.status) === group);
  const makes = Array.from(new Set(cars.map((c) => c.make).filter(Boolean))).sort();

  return (
    <div className="sm:pl-52 space-y-4" data-testid="cars-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Stoc auto</div>
          <h1 className="font-display text-3xl tracking-tight font-medium">Mașini</h1>
        </div>
        <span className="text-sm text-slate-500 tabular" data-testid="cars-count">{visible.length} rezultate</span>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="cars-group-filter">
        {CAR_GROUPS.map((g) => (
          <button
            key={g.key}
            onClick={() => setGroup(g.key)}
            data-testid={`cars-group-${g.key}`}
            className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              group === g.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >
            {g.label}
            <span className={`tabular text-[10px] px-1.5 rounded-full ${group === g.key ? "bg-white/20" : "bg-slate-100"}`} data-testid={`cars-group-count-${g.key}`}>{counts[g.key]}</span>
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <input
          data-testid="cars-search"
          placeholder="Caută marcă, model, VIN..."
          value={filter.q}
          onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
          className="flex-1 min-w-[180px] bg-white border border-slate-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
        />
        <select
          data-testid="cars-filter-status"
          value={filter.status}
          onChange={(e) => { setFilter((f) => ({ ...f, status: e.target.value })); if (e.target.value) setGroup("all"); }}
          className="bg-white border border-slate-200 rounded-full px-3 py-2 text-sm"
        >
          <option value="">Toate statusurile</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {makes.length > 0 && (
          <select
            data-testid="cars-filter-make"
            value={filter.make}
            onChange={(e) => setFilter((f) => ({ ...f, make: e.target.value }))}
            className="bg-white border border-slate-200 rounded-full px-3 py-2 text-sm"
          >
            <option value="">Toate mărcile</option>
            {makes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
      </div>

      {visible.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          <CarIcon className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 font-display text-lg">Niciun rezultat</div>
          <div className="text-sm text-slate-500 mt-1">{cars.length === 0 ? "Apasă pe + pentru a adăuga prima mașină." : "Nicio mașină în această categorie."}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {visible.map((c) => (
          <Link
            data-testid={`car-card-${c.id}`}
            key={c.id}
            to={`/cars/${c.id}`}
            className={`group bg-white border border-slate-200 rounded-2xl p-4 hover:border-slate-400 hover:-translate-y-0.5 transition-all ${c.status === "vanduta" && group === "all" ? "opacity-60" : ""}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg font-medium">{c.make} {c.model}</div>
                <div className="text-xs text-slate-500">{c.year || "—"}{c.vin ? ` · VIN ${c.vin}` : ""}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {c.vat_deductible && (
                    <span className="text-[9px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                      TVA deductibil
                    </span>
                  )}
                  {(() => {
                    const ds = DOCUMENTS_STATUS_MAP[c.documents_status || "pending"];
                    // Show the badge always when in_transport, or whenever non-default state
                    const showAlways = c.status === "in_transport" || (c.documents_status && c.documents_status !== "pending");
                    if (!showAlways) return null;
                    return (
                      <span className={`text-[9px] uppercase tracking-widest ${ds.bg} ${ds.text} border ${ds.border} px-1.5 py-0.5 rounded-full`}>
                        {ds.emoji} {ds.short}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <StatusBadge status={c.status} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-100">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Investiție</div>
                <div className="text-sm font-medium tabular">{ron(c.investment_total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Profit</div>
                <div className={`text-sm font-medium tabular ${c.profit_estimated >= 0 ? "text-emerald-600" : "text-red-600"}`}>{ron(c.profit_estimated)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Zile</div>
                <div className="text-sm font-medium tabular">{c.days_in_stock}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
